import { assert, log, error } from './logger.js';
import { settings } from './settings.js';
import { run } from '../ui/ui_queueable.js';
import { window } from '../window.js';

const kWSUrl = `wss://${window.location.host}/wss`;

/**
 * Web socket connection for messages exchange between server/clients.
 */
class WS {
  register(type, pipe) {
    this.#pipes.set(type, pipe);
  }

  unregister(type) {
    this.#pipes.delete(type);
  }

  shutdownIfNoPipes() {
    if (this.constructor.ws) {
      this.constructor.ws.close();
      this.constructor.ws = null;
    }
  }

  /**
   * Sends a message to the server.
   */
  async send({ pipeType, pipeId, messageType }) {
    let ws = await this.#getWs();
    if (!ws) {
      assert(
        settings.appSyncDisabled,
        `[ws] failed to send ${messageType} message, no web socket has been instantiated`
      );
      return;
    }

    let id = `${pipeType}:${pipeId}`;
    let data = {
      id,
      type: messageType,
    };

    // Send a message if socket is ready, otherwise put if into a buffer to
    // process later.
    if (ws.readyState == WebSocket.OPEN) {
      log(
        `[ws] send ${messageType} message for ${id}, ready state: ${ws.readyState}`
      );
      ws.send(JSON.stringify(data));
    } else {
      log(
        `[ws] bufferize ${messageType} message for ${id}, ready state: ${ws.readyState}`
      );
      this.#buf.push(data);
    }
  }

  #getWs() {
    if (this.constructor.ws || settings.appSyncDisabled) {
      return this.constructor.ws;
    }
    try {
      this.constructor.ws = new WebSocket(kWSUrl);
      this.constructor.ws.onmessage = async ev => this.#onMessage(ev);
      this.constructor.ws.onopen = () => this.#onOpen();
      this.constructor.ws.onclose = () => {
        log(
          `[ws] socket closed, ready state: ${this.constructor.ws?.readyState}`
        );
        this.constructor.ws = null;
      };
      this.constructor.ws.onerror = e =>
        error(
          `[ws] socket error, ready state: ${this.constructor.ws?.readyState}`,
          e
        );
    } catch (e) {
      error(`[ws] failed to create web socket for ${kWSUrl}`, e);
      this.constructor.ws = null;
    }
    return this.constructor.ws;
  }

  async #onMessage(ev) {
    try {
      let msg = JSON.parse(ev.data);
      log(`[ws] '${msg.type}' notification for ${msg.id}`);

      let [pipeType, pipeId] = msg.id.split(':');
      let pipe = this.#pipes.get(pipeType);
      assert(
        pipe,
        `[ws] no ${pipeType} pipe is registered for ${msg.id} message`
      );
      await run(() =>
        pipe?.on({
          id: pipeId,
          type: msg.type,
          data: msg.data,
        })
      );
    } catch (e) {
      error(`[ws] message processing error`, e);
    }
  }

  #onOpen() {
    if (!this.constructor.ws) {
      return; // socket was requested to close, ignore onopen.
    }

    log(
      `[ws] socket connected, ready state: ${this.constructor.ws.readyState}`
    );

    // Send buffered messages if any.
    if (this.constructor.ws.readyState == WebSocket.OPEN) {
      for (let msg of this.#buf) {
        log(`[ws] send buffered ${msg.type} message ${msg.id}`);
        this.constructor.ws.send(JSON.stringify(msg));
      }
      this.#buf = [];
    }
  }

  #pipes = new Map();
  #buf = [];
}

export const ws = new WS();
